/**
 * One description of a subscription for the whole admin dashboard.
 *
 * The backend already derives the truth — `subscriptionStatus`, `hasAccess`,
 * the trial window and the renewal date all come from
 * `GET /api/admin/user-stats` and the user list, computed from the same code
 * that answers the user's own phone. This file only decides how to *say* it, so
 * a badge in the list cannot claim something different from the detail page.
 *
 * Do not re-derive access here. `hasAccess` is the answer; `subscriptionStatus`
 * is the reason. Three statuses keep access on purpose:
 *   • cancelled     — auto-renew is off, the paid period is still running
 *   • grace_period  — the charge failed, the store granted a grace window
 *   • billing_issue — the charge failed and the store is retrying
 *
 * These values must match the backend enum in
 * smartZman-Backend/utils/userUtils/helper/subscriptionState.js exactly — an
 * unknown status renders as "Unknown", not as a guess.
 */

export const SUBSCRIPTION_STATUS_META = {
  trial: {
    label: "Trial",
    tone: "info",
    hint: "Inside the store free trial",
  },
  active: {
    label: "Active",
    tone: "success",
    hint: "Paid and renewing",
  },
  promotional: {
    label: "Free (promotional)",
    tone: "info",
    hint: "Free access granted in RevenueCat. Not a paid subscription: it does not renew and nothing is charged",
  },
  grace_period: {
    label: "Grace period",
    tone: "warning",
    hint: "The charge failed and the store granted a grace window. Access continues until it ends",
  },
  billing_issue: {
    label: "Billing issue",
    tone: "warning",
    hint: "The store could not charge and is retrying. Access continues to the normal expiry",
  },
  cancelled: {
    label: "Cancelling",
    tone: "warning",
    hint: "Auto-renew is off (or the purchase was refunded). Access continues while it lasts",
  },
  expired: {
    label: "Expired",
    tone: "danger",
    hint: "No access. Schedule changes are blocked (403 SUBSCRIPTION_REQUIRED)",
  },
  none: {
    label: "No subscription",
    tone: "neutral",
    hint: "Never subscribed — this user sees the paywall",
  },
};

// MUI chip colours, keyed by tone so the palette lives in one place.
export const TONE_SX = {
  success: { bgcolor: "rgba(16,185,129,0.15)", color: "#10b981" },
  info: { bgcolor: "rgba(99,102,241,0.15)", color: "#818cf8" },
  warning: { bgcolor: "rgba(245,158,11,0.15)", color: "#f59e0b" },
  danger: { bgcolor: "rgba(239,68,68,0.15)", color: "#ef4444" },
  neutral: { bgcolor: "rgba(148,163,184,0.15)", color: "#94a3b8" },
};

// Tailwind equivalents for the pages that are not MUI.
export const TONE_CLASSES = {
  success: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  info: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  warning: "bg-amber-50 text-amber-700 ring-amber-600/20",
  danger: "bg-red-50 text-red-700 ring-red-600/20",
  neutral: "bg-slate-100 text-slate-500 ring-slate-400/20",
};

// Only for a status the backend sent that this file does not know — i.e. the
// two enums have drifted. Saying "Unknown" is deliberate: guessing "expired"
// would tell an admin a paying customer has no access.
const UNKNOWN = {
  status: null,
  label: "Unknown",
  tone: "neutral",
  hint: "The backend reported a subscription status this dashboard does not recognise",
  hasAccess: false,
  willRenew: false,
};

const NONE = {
  status: "none",
  label: SUBSCRIPTION_STATUS_META.none.label,
  tone: SUBSCRIPTION_STATUS_META.none.tone,
  hint: SUBSCRIPTION_STATUS_META.none.hint,
  hasAccess: false,
  willRenew: false,
};

/**
 * @param {object|null|undefined} subscription the `subscription` object as the
 *   admin API returns it (list row or user detail — both carry the same fields)
 */
export function describeSubscription(subscription) {
  // The user list omits `subscription` entirely for a user with no row, and
  // that is exactly what "never subscribed" looks like.
  if (!subscription) return NONE;

  const status = subscription.subscriptionStatus;
  const meta = SUBSCRIPTION_STATUS_META[status];

  if (!meta) return UNKNOWN;

  return {
    status,
    label: meta.label,
    tone: meta.tone,
    hint: meta.hint,
    hasAccess: Boolean(subscription.hasAccess),
    // Canonical names first, deprecated aliases as the fallback.
    willRenew: Boolean(subscription.willRenew ?? subscription.autoRenew),
    isPromotional: status === "promotional" || Boolean(subscription.isPromotional),
    trialStart: subscription.trialStart || null,
    trialEnd: subscription.trialEndAt || subscription.trialEnd || null,
    currentPeriodEnd: subscription.currentPeriodEnd || null,
    productId:
      subscription.productIdentifier || subscription.productId || null,
    platform: subscription.platform || null,
    // "store" — verified with RevenueCat · "legacy_trial" — the server-clock
    // trial that predates store trials.
    source: subscription.source || null,
    isStoreVerified: subscription.source === "store",
  };
}

export const PLATFORM_LABELS = {
  app_store: "App Store",
  play_store: "Google Play",
  amazon: "Amazon",
  stripe: "Stripe",
  promotional: "Promotional",
};
